import { useEffect, useMemo, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { DashboardLayout } from "@/components/DashboardLayout";
import { api, API_URL, formatApiError } from "@/lib/api";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { LabelPreview } from "@/components/LabelPreview";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import { Download, Printer, History } from "lucide-react";

const SIZES = [30, 50, 100, 200, 500];

function genBatch() {
  const d = new Date();
  const p = (n) => String(n).padStart(2, "0");
  return `L${d.getFullYear().toString().slice(-2)}${p(d.getMonth() + 1)}${p(d.getDate())}-${Math.floor(Math.random() * 900 + 100)}`;
}

export default function GenerateLabel() {
  const { user, refresh } = useAuth();
  const [liquids, setLiquids] = useState([]);
  const [customers, setCustomers] = useState([]);
  const [liquidId, setLiquidId] = useState("");
  const [bottle, setBottle] = useState(50);
  const [batch, setBatch] = useState("");
  const [nicotine, setNicotine] = useState("");
  const [quantity, setQuantity] = useState(1);
  const [customerName, setCustomerName] = useState("");
  const [showSuggest, setShowSuggest] = useState(false);
  const [preview, setPreview] = useState(null);
  const [previewErr, setPreviewErr] = useState("");
  const [loading, setLoading] = useState(false);
  const previewRef = useRef();

  useEffect(() => {
    api.get("/liquids").then(({ data }) => {
      setLiquids(data);
      if (data.length && !liquidId) {
        const l = data[0];
        setLiquidId(l.id);
        setBatch(l.batch_number || genBatch());
        setNicotine(l.nicotine ?? "");
      }
    });
    api.get("/customers").then(({ data }) => setCustomers(data)).catch(() => {});
    // eslint-disable-next-line
  }, []);

  const selectedLiquid = useMemo(() => liquids.find((l) => l.id === liquidId), [liquids, liquidId]);

  const suggestions = useMemo(() => {
    if (!customerName) return customers.slice(0, 6);
    const q = customerName.toLowerCase();
    return customers.filter((c) => c.name.toLowerCase().includes(q)).slice(0, 6);
  }, [customerName, customers]);

  const refreshPreview = async () => {
    if (!liquidId) { setPreview(null); return; }
    try {
      setPreviewErr("");
      const { data } = await api.post("/labels/preview", {
        liquid_id: liquidId,
        bottle_size_ml: Number(bottle),
        batch_number: batch,
        nicotine_mg: nicotine === "" ? null : Number(nicotine),
        quantity: 1,
      });
      setPreview(data);
    } catch (e) {
      setPreview(null);
      setPreviewErr(formatApiError(e.response?.data?.detail));
    }
  };

  useEffect(() => { refreshPreview(); }, [liquidId, bottle, batch, nicotine]); // eslint-disable-line

  const onLiquidChange = (v) => {
    setLiquidId(v);
    const l = liquids.find((x) => x.id === v);
    if (l) {
      setBatch(l.batch_number || genBatch());
      setNicotine(l.nicotine ?? "");
    }
  };

  const downloadPdf = async () => {
    const isAdmin = user?.role === "admin";
    const qty = Number(quantity) || 1;
    const balance = user?.credits ?? 0;
    if (!isAdmin && balance < qty) {
      toast.error(`Crédits insuffisants : ${balance} disponible(s), ${qty} requis. Rechargez votre compte.`);
      return;
    }
    if (!liquidId) { toast.error("Sélectionnez un liquide"); return; }
    setLoading(true);
    try {
      const token = localStorage.getItem("vape_token");
      const resp = await fetch(`${API_URL}/labels/pdf`, {
        method: "POST",
        credentials: "include",
        headers: {
          "Content-Type": "application/json",
          ...(token ? { Authorization: `Bearer ${token}` } : {}),
        },
        body: JSON.stringify({
          liquid_id: liquidId,
          bottle_size_ml: Number(bottle),
          batch_number: batch,
          nicotine_mg: nicotine === "" ? null : Number(nicotine),
          quantity: qty,
          customer_name: customerName.trim(),
        }),
      });
      if (!resp.ok) {
        const txt = await resp.text();
        try { throw new Error(JSON.parse(txt).detail || "Échec"); } catch { throw new Error(txt || "Échec"); }
      }
      const blob = await resp.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url; a.download = `etiquette_${selectedLiquid?.brand || "liquide"}_${bottle}ml.pdf`;
      a.click();
      URL.revokeObjectURL(url);
      toast.success(`PDF téléchargé — ${isAdmin ? "0" : qty} crédit${qty > 1 ? "s" : ""} consommé${qty > 1 ? "s" : ""}`);
      api.get("/customers").then(({ data }) => setCustomers(data)).catch(() => {});
      if (typeof refresh === "function") await refresh();
    } catch (e) {
      toast.error(e.message);
    } finally {
      setLoading(false);
    }
  };

  const printBrowser = () => {
    if (!previewRef.current) return;
    const html = previewRef.current.outerHTML;
    const w = window.open("", "_blank", "width=800,height=600");
    w.document.write(`<!doctype html><html><head><title>Étiquette</title>
      <link rel="stylesheet" href="https://fonts.googleapis.com/css2?family=IBM+Plex+Sans:wght@400;600;700&family=JetBrains+Mono&family=Cormorant+Garamond:wght@600;700&display=swap">
      <style>body{margin:0;padding:20px;background:#fff;}@media print{body{padding:0;}}</style>
      </head><body>${html}<script>window.onload=()=>{setTimeout(()=>window.print(),300)}</script></body></html>`);
    w.document.close();
  };

  return (
    <DashboardLayout title="Générer une étiquette">
      {user?.role !== "admin" && (user?.credits ?? 0) < (Number(quantity) || 1) && (
        <div className="mb-6 border border-red-500/40 bg-red-500/5 p-4 text-sm text-zinc-300 flex items-center justify-between" data-testid="gen-low-credits-banner">
          <span>
            Crédits insuffisants : <span className="font-bold text-white">{user?.credits ?? 0}</span> disponible(s) — {Number(quantity) || 1} requis pour l'impression.
          </span>
          <Link to="/credits" className="bg-red-500 hover:bg-red-600 text-white px-3 py-1.5 text-xs font-medium">Recharger</Link>
        </div>
      )}
      <div className="grid grid-cols-1 lg:grid-cols-12 gap-6">
        {/* Form */}
        <div className="lg:col-span-5 border border-zinc-800 p-6 space-y-5">
          <div>
            <label className="block text-xs font-mono uppercase tracking-widest text-zinc-500 mb-2">Liquide</label>
            {liquids.length === 0 ? (
              <div className="text-sm text-zinc-500">Aucun liquide.{" "}
                <Link to="/liquids" className="text-red-500 hover:text-red-400">Ajouter</Link>
              </div>
            ) : (
              <Select value={liquidId} onValueChange={onLiquidChange}>
                <SelectTrigger data-testid="gen-liquid-select" className="rounded-none bg-zinc-900 border-zinc-800"><SelectValue /></SelectTrigger>
                <SelectContent className="bg-zinc-950 border-zinc-800 rounded-none">
                  {liquids.map((l) => (
                    <SelectItem key={l.id} value={l.id} data-testid={`gen-liquid-opt-${l.id}`}>
                      {l.brand} — {l.flavor}{l.dluo ? ` (DLUO ${l.dluo})` : ""}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            )}
          </div>

          <div>
            <label className="block text-xs font-mono uppercase tracking-widest text-zinc-500 mb-2">Taille du flacon</label>
            <div className="grid grid-cols-5 gap-0 border border-zinc-800">
              {SIZES.map((s) => (
                <button
                  key={s}
                  data-testid={`gen-size-${s}`}
                  onClick={() => setBottle(s)}
                  className={`py-3 text-sm font-semibold border-r last:border-r-0 border-zinc-800 transition-colors ${bottle === s ? "bg-red-500 text-white" : "bg-zinc-900 text-zinc-400 hover:text-white"}`}
                >
                  {s}
                </button>
              ))}
            </div>
            <div className="text-[10px] font-mono uppercase tracking-widest text-zinc-500 mt-1">ml</div>
          </div>

          <div>
            <label className="block text-xs font-mono uppercase tracking-widest text-zinc-500 mb-2">Numéro de lot</label>
            <div
              data-testid="gen-batch"
              className="rounded-none bg-zinc-900 border border-zinc-800 font-mono text-sm text-zinc-300 px-3 py-2 min-h-[36px] flex items-center"
            >
              {batch || <span className="text-zinc-600">—</span>}
            </div>
            <div className="text-[10px] text-zinc-500 mt-1 font-mono">
              Repris automatiquement du liquide (non modifiable ici)
            </div>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-xs font-mono uppercase tracking-widest text-zinc-500 mb-2">Nicotine (mg/ml)</label>
              <Input data-testid="gen-nic" type="number" step="0.1" min="0" value={nicotine} onChange={(e) => setNicotine(e.target.value)}
                className="rounded-none bg-zinc-900 border-zinc-800 font-mono" placeholder="0" />
            </div>
            <div>
              <label className="block text-xs font-mono uppercase tracking-widest text-zinc-500 mb-2">Quantité</label>
              <Input data-testid="gen-qty" type="number" min="1" max="200" value={quantity} onChange={(e) => setQuantity(e.target.value)}
                className="rounded-none bg-zinc-900 border-zinc-800 font-mono" />
            </div>
          </div>

          <div className="relative">
            <label className="block text-xs font-mono uppercase tracking-widest text-zinc-500 mb-2">Nom du client (optionnel)</label>
            <Input
              data-testid="gen-customer"
              value={customerName}
              onChange={(e) => { setCustomerName(e.target.value); setShowSuggest(true); }}
              onFocus={() => setShowSuggest(true)}
              onBlur={() => setTimeout(() => setShowSuggest(false), 150)}
              placeholder="Ex. Jean Dupont"
              className="rounded-none bg-zinc-900 border-zinc-800"
            />
            {showSuggest && suggestions.length > 0 && (
              <div className="absolute z-10 top-full left-0 right-0 bg-zinc-950 border border-zinc-800 border-t-0 max-h-56 overflow-y-auto">
                {suggestions.map((s) => (
                  <button
                    key={s.name}
                    type="button"
                    onMouseDown={() => setCustomerName(s.name)}
                    className="w-full text-left px-3 py-2 text-sm hover:bg-zinc-900 flex justify-between items-center"
                  >
                    <span>{s.name}</span>
                    <span className="text-[10px] font-mono text-zinc-500">{s.count}×</span>
                  </button>
                ))}
              </div>
            )}
            <div className="text-[10px] text-zinc-500 mt-1 font-mono">Utilisé pour retrouver le client dans l'historique</div>
          </div>

          <div className="pt-2 flex gap-3">
            <Button data-testid="gen-pdf-btn" onClick={downloadPdf} disabled={loading || !liquidId}
              className="flex-1 bg-red-500 hover:bg-red-600 rounded-none">
              <Download className="w-4 h-4 mr-2" />{loading ? "Génération…" : "Télécharger PDF"}
            </Button>
            <Button data-testid="gen-print-btn" onClick={printBrowser} disabled={!preview} variant="outline"
              className="rounded-none border-zinc-700 bg-transparent text-zinc-100 hover:bg-zinc-900 hover:text-white">
              <Printer className="w-4 h-4 mr-2" /> Imprimer
            </Button>
          </div>

          <Link to="/history" className="flex items-center gap-2 text-xs font-mono text-zinc-500 hover:text-red-500 pt-2 border-t border-zinc-800">
            <History className="w-3.5 h-3.5" /> Consulter l'historique des étiquettes →
          </Link>
        </div>

        {/* Preview */}
        <div className="lg:col-span-7 border border-zinc-800 p-6 flex flex-col">
          <div className="font-mono text-[10px] uppercase tracking-widest text-zinc-500 mb-4">Aperçu réel · {preview?.label_width_mm}×{preview?.label_height_mm} mm</div>
          <div className="flex-1 flex items-center justify-center bg-zinc-900/40 p-8 border border-dashed border-zinc-800">
            {preview ? (
              <LabelPreview ref={previewRef} data={preview} scale={4} />
            ) : (
              <div className="text-sm text-zinc-500 text-center">
                {previewErr || "Choisissez un liquide pour afficher l'aperçu."}
              </div>
            )}
          </div>
          {preview?.dluo_date && (
            <div className="mt-4 text-xs font-mono text-zinc-500">
              DLUO du liquide : <span className="text-white font-semibold">{preview.dluo_date}</span>
            </div>
          )}
        </div>
      </div>
    </DashboardLayout>
  );
}
